import { ToolboxManager } from '../../ToolboxManager/ToolboxManager'
import { getStore } from 'app/state/storeRegistry'
import { WorkflowPayload } from 'features/workflows/types'
import {
  Workflow as WorkflowEngine,
  Toolbox,
  BQConnection,
  DBricksConnection,
  PGConnection,
  Provider,
  RSConnection,
  SFConnection,
  OracleConnection,
  WorkflowEnvVars,
  WorkflowTableSchemas,
  LocationType
} from '@cloud-native/workflows-engine'
import settings from 'settings'
import { Connection } from 'features/connections'
import { WorkflowCodeType, StoredProcedureScope } from '@cloud-native/workflows-engine'
import { connectionsQueryKeys } from 'features/connections/api/connectionsQueries'
import { queryClient } from '../../../../../queryClient'
import { WorkflowsExecutionStatus } from '../../../enums'
import { JobStatus } from '../../../../jobs/types'
import { getCompleteNodeDetails } from './utils'
import { ProviderType } from 'features/connections'
import { cleanChar } from 'features/common/utils/strings'
import { getAnalyticsToolboxLocation } from 'features/connections/api/connections'
import { calculateRegion } from 'features/workflows/utils/regionUtils'
import { extractNameFromQueryCall } from 'features/workflows/utils/formatCodeSnippets'
import { FeatureFlag, selectActualFeatureFlags } from 'app/ui/useFeatureFlag'
export class WorkflowEngineClient {
  constructor(private readonly toolboxManager: ToolboxManager) {}

  #atLocation = ''

  async loadATLocation(workflow: WorkflowPayload) {
    const tenantId = getStore().getState().users.me?.user_metadata.tenant_id || ''
    const tempLocation = workflow?.tempLocation
    const region = calculateRegion({
      tempLocation,
      provider: workflow.config.connectionProvider as unknown as ProviderType,
      tenantId
    })
    try {
      this.#atLocation = cleanChar(
        await getAnalyticsToolboxLocation({ connectionId: workflow.connectionId, region }),
        '`'
      )
    } catch (error) {
      // When viewing a shared workflow (in preview) the user might not have access to conn
    }
  }

  getEnvVarsForProvider(workflow: WorkflowPayload) {
    const connection = queryClient.getQueryData<Connection>(connectionsQueryKeys.connection(workflow.connectionId))

    const dataExportDefaultGCSBucket = workflow.dataExportsGcsBucketName
    const userMetadata = getStore().getState().users.me?.user_metadata
    const tenantDomain = userMetadata?.tenant_domain || ''

    let bigqueryProjectId: string | undefined = undefined
    let bigqueryRegion: string | undefined = undefined

    if (workflow.config.connectionProvider === Provider.BigQuery) {
      // BigQuery project and region where the workflow is executed.
      // Note that the tempLocation project coincides with the billing project for BigQuery OAuth connections,
      // and also for the CDW connection, but may differ for Service Account connections.
      bigqueryProjectId = connection?.config.billing_project

      /* ==== START TEMPORARY BLOCK === */
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const tenantId = userMetadata?.tenant_id || ''
      const tempLocation = workflow?.tempLocation
      const region = calculateRegion({
        tempLocation,
        provider: connection?.provider_id as ProviderType,
        tenantId
      })
      /* ==== END TEMPORARY BLOCK === */

      bigqueryRegion = region
    }

    return {
      apiBaseUrl: settings.apiBaseUrl,
      analyticsToolboxDataset: this.#atLocation || connection?.atLocation,
      analyticsToolboxVersion: connection?.atVersion,
      dataExportDefaultGCSBucket,
      bigqueryProjectId,
      bigqueryRegion,
      isCartoDW: connection?.carto_dw,
      domain: tenantDomain,
      mapProvider: getStore().getState().account?.ldsQuotasStats?.[0]?.providers
    } as WorkflowEnvVars
  }

  get atLocation() {
    return this.#atLocation
  }

  getWorkflowEngineConnection({
    connectionName,
    config: { connectionProvider },
    tempLocation,
    workspaceLocation,
    extensionsLocation
  }: {
    connectionName: string
    config: { connectionProvider: Provider }
    tempLocation: { fqn: string }
    workspaceLocation: { fqn: string }
    extensionsLocation: { fqn: string }
  }) {
    // Get feature flags to check if CTEs should be used
    const featureFlags = selectActualFeatureFlags(getStore().getState())
    const useCTEs = Boolean(featureFlags[FeatureFlag.WorkflowsUseCTEs])

    let connection
    if (connectionProvider === Provider.BigQuery) {
      connection = new BQConnection(connectionName, tempLocation?.fqn, workspaceLocation?.fqn, extensionsLocation?.fqn)
    } else if (connectionProvider === Provider.Snowflake) {
      connection = new SFConnection(connectionName, tempLocation?.fqn, workspaceLocation?.fqn, extensionsLocation?.fqn)
    } else if (connectionProvider === Provider.Postgres) {
      connection = new PGConnection(connectionName, tempLocation?.fqn, workspaceLocation?.fqn, extensionsLocation?.fqn)
    } else if (connectionProvider === Provider.Redshift) {
      connection = new RSConnection(connectionName, tempLocation?.fqn, workspaceLocation?.fqn, extensionsLocation?.fqn)
    } else if (connectionProvider === Provider.DatabricksRest) {
      connection = new DBricksConnection(
        connectionName,
        tempLocation?.fqn,
        workspaceLocation?.fqn,
        extensionsLocation?.fqn
      )
    } else if (connectionProvider === Provider.Oracle) {
      connection = new OracleConnection(
        connectionName,
        tempLocation?.fqn,
        workspaceLocation?.fqn,
        extensionsLocation?.fqn
      )
    } else {
      throw new Error(`Connection provider is not compatible: ${connectionProvider}`)
    }

    // Set the useCTEs property based on the feature flag
    connection.useCTEs = useCTEs

    return connection
  }

  getWorkflowEngine(workflow: WorkflowPayload) {
    return WorkflowEngine.fromDiagram(workflow.id, workflow.config, this.toolboxManager.toolbox as Toolbox)
  }

  getAsStoredProcedure(
    workflow: WorkflowPayload,
    scope: StoredProcedureScope,
    schemas: WorkflowTableSchemas | undefined = undefined
  ) {
    return this.getWorkflowEngine(workflow).getAsStoredProcedure(
      this.getWorkflowEngineConnection(workflow),
      { ...this.getEnvVarsForProvider(workflow), accessToken: workflow.token },
      workflow.config.useCache,
      true,
      scope,
      schemas
    )
  }

  async getAsDirectSQL(
    workflow: WorkflowPayload,
    scope: StoredProcedureScope,
    schemas: WorkflowTableSchemas | undefined = undefined
  ): Promise<string | null> {
    // Get the connection to determine provider
    const connectionData = queryClient.getQueryData<Connection>(connectionsQueryKeys.connection(workflow.connectionId))

    // Only BigQuery supports direct SQL for now
    if (connectionData?.provider_id !== ProviderType.BIGQUERY) {
      return null
    }

    const connection = this.getWorkflowEngineConnection(workflow)
    const envVars = { ...this.getEnvVarsForProvider(workflow), accessToken: workflow.token }
    const workflowEngine = this.getWorkflowEngine(workflow)

    // Cast to BigQuery connection and call the method directly
    return (connection as BQConnection).getAsDirectSQL(workflowEngine, envVars, scope, schemas)
  }

  getStoredProcedureOutputIsTableFullContent(workflow: WorkflowPayload, scope: StoredProcedureScope) {
    return this.getWorkflowEngine(workflow).getStoredProcedureOutputIsTableFullContent(
      this.getWorkflowEngineConnection(workflow),
      scope
    )
  }

  getStoredProcedureCacheInvalidationQueries(workflow: WorkflowPayload, call: string) {
    return this.getWorkflowEngineConnection(workflow).getStoredProcedureCacheInvalidationQueries(
      extractNameFromQueryCall(call)
    )
  }

  getRemoveStoredProcedureQueries(workflow: WorkflowPayload, call: string) {
    return this.getWorkflowEngine(workflow).getRemoveStoredProcedureQueries(
      this.getWorkflowEngineConnection(workflow),
      extractNameFromQueryCall(call)
    )
  }

  getOutputTables(workflow: WorkflowPayload, codeType: WorkflowCodeType = WorkflowCodeType.DryRun) {
    return this.getWorkflowEngine(workflow).getOutputTables(this.getWorkflowEngineConnection(workflow), codeType)
  }

  getWorkflowCode({
    workflow,
    codeType,
    schemas
  }: {
    workflow: WorkflowPayload
    codeType?: WorkflowCodeType
    schemas?: WorkflowTableSchemas
  }) {
    const workflowCodeType = workflow.config.useCache ? WorkflowCodeType.Full : WorkflowCodeType.FullNoCache

    return this.getWorkflowEngine(workflow).getWorkflowCode(
      this.getWorkflowEngineConnection(workflow),
      { ...this.getEnvVarsForProvider(workflow), accessToken: workflow.token },
      codeType || workflowCodeType,
      schemas
    )
  }

  async getNodeDetails(
    workflow: WorkflowPayload,
    workflowState: WorkflowsExecutionStatus,
    error: JobStatus['error'] | null,
    existingOutputTables: string[] | null,
    schemas?: WorkflowTableSchemas
  ) {
    const workflowEngineConnection = this.getWorkflowEngineConnection(workflow)
    const workflowEngine = this.getWorkflowEngine(workflow)
    const workflowCodeType = workflow.config.useCache ? WorkflowCodeType.Full : WorkflowCodeType.FullNoCache

    let errorNode

    const envVars = this.getEnvVarsForProvider(workflow)
    if (error && error.msg) {
      errorNode = await workflowEngine.getComponentFromApiResponse(
        envVars,
        workflowEngineConnection,
        workflowCodeType,
        error.msg
      )
    }
    return await getCompleteNodeDetails(
      workflowEngine,
      workflowEngineConnection,
      workflowState,
      error,
      errorNode,
      workflow,
      existingOutputTables,
      schemas
    )
  }

  getExtensionComponentsQuery(workflow: WorkflowPayload): string {
    return this.getWorkflowEngineConnection(workflow).getExtensionComponentsQuery() as string
  }

  getAsTasksList(workflow: WorkflowPayload, schemas: WorkflowTableSchemas | undefined = undefined) {
    return this.getWorkflowEngine(workflow).getAsTasksList(
      this.getWorkflowEngineConnection(workflow),
      {
        ...this.getEnvVarsForProvider(workflow),
        accessToken: workflow.token
      },
      workflow.config.useCache,
      StoredProcedureScope.Api,
      schemas
    )
  }

  getTempStorageSetupQueries(workflow: WorkflowPayload, options: { location?: string }) {
    return this.getWorkflowEngineConnection(workflow).getStorageSetupQueries(options)
  }

  getTableListQuery(workflow: WorkflowPayload, filter: string, locationType?: LocationType) {
    return this.getWorkflowEngine(workflow).getTableListQuery(
      this.getWorkflowEngineConnection(workflow),
      filter,
      locationType
    )
  }

  getRequiredGrants(workflow: WorkflowPayload) {
    const defaultGrants = {
      grants: [],
      referers: [],
      // Tokens API doesn't support empty allowed_apis, use `sql` as a filler
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      allowed_apis: ['sql' as any]
    }
    const requiredGrants = this.getWorkflowEngine(workflow).getRequiredGrants(
      this.getWorkflowEngineConnection(workflow)
    )

    if (requiredGrants && requiredGrants.allowed_apis.length === 0) {
      requiredGrants.allowed_apis = defaultGrants.allowed_apis
    }

    // If user has exported procedure we need to grant additional token permissions
    if (workflow.config.procedure && workflow.config.procedure.call) {
      // TODO: remove this when the sql jobs api requires the `sql` allowed api
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      requiredGrants?.allowed_apis.push('batch' as any)

      // Add `sql` allowed api needed for API execution; avoid duplicates since `sql` may
      // already be present as a filler from defaultGrants
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (requiredGrants && !requiredGrants.allowed_apis.includes('sql' as any)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        requiredGrants?.allowed_apis.push('sql' as any)
      }
      requiredGrants?.grants.push({
        connection_name: workflow.connectionName,
        source: workflow.config.procedure.call
      })
    }

    return requiredGrants || defaultGrants
  }
}
